/**
 * Videos left playing or loading behind a dialog keep the browser's few connections per host busy: the intake then
 * waits seconds for its own code ("Opening this part of the Studio…"). While a dialog is open, the page's other
 * videos pause and stop downloading; when it closes they get their sources back and what was playing plays on.
 */
interface Suspended {
  video: HTMLVideoElement;
  src: string | null;
  sources: [HTMLSourceElement, string][];
  playing: boolean;
}

const NETWORK_LOADING = 2;
let suspended: Suspended[] = [];

export function suspendBackgroundVideos(keep: Element): void {
  for (const video of Array.from(document.querySelectorAll("video"))) {
    if (keep.contains(video) || suspended.some((item) => item.video === video)) continue;
    const playing = !video.paused;
    const loading = video.networkState === NETWORK_LOADING;
    if (!playing && !loading) continue;
    if (playing) video.pause();
    const entry: Suspended = { video, src: null, sources: [], playing };
    if (loading) {
      // Taking the sources away and reloading is the only way to make a browser drop a media download.
      entry.src = video.getAttribute("src");
      video.removeAttribute("src");
      for (const source of Array.from(video.querySelectorAll("source"))) {
        entry.sources.push([source, source.getAttribute("src") || ""]);
        source.removeAttribute("src");
      }
      video.load();
    }
    suspended.push(entry);
  }
}

export function resumeBackgroundVideos(): void {
  const items = suspended;
  suspended = [];
  for (const { video, src, sources, playing } of items) {
    if (!video.isConnected) continue;
    if (src !== null || sources.length) {
      if (src !== null) video.setAttribute("src", src);
      for (const [source, value] of sources) source.setAttribute("src", value);
      video.load();
    }
    if (playing) void video.play().catch(() => {});
  }
}
